import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map,
} from "@vis.gl/react-google-maps";
import { memo, useEffect, useMemo, useState } from "react";

import { useDriverRoute } from "@web-app/api";
import { DateTimeTz, Marker, Skeleton } from "@web-app/components";
import config from "@web-app/config";
import { Order } from "@web-app/types";
import { getMetersToMiles } from "@web-app/utils/helpers";

import { getOrderIcon, getPolylineOptions } from "../../DispatchMap/utils";
import useOrderSidebar from "../useOrderSidebar";

import {
  EVENT_COLORS,
  EventBase,
  EventMarker,
  MapPolyline,
  MapSetup,
  findClosestLocation,
  getRoutePositionColor,
} from "./driverRouteUtils";
import { useDriverRouteMapState } from "./useDriverRouteMapState";

interface DriverRouteProps {
  order: Order;
}

const DriverRoute = memo(function DriverRoute({ order }: DriverRouteProps) {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [expandedMapInstance, setExpandedMapInstance] =
    useState<google.maps.Map | null>(null);
  const { assigneesMap } = useOrderSidebar();

  const isLoaded = true; // API provider will handle loading

  const { data: driverRoute, isSuccess, isPending } = useDriverRoute(order.id);

  let assignee = "Unknown";

  if (order.assigneeId) {
    assignee = assigneesMap.get(order.assigneeId)?.name ?? "Unknown";
  }

  const {
    actualDistance,
    hoveredEventMarker,
    hoveredPoint,
    isExpanded,
    routeCoordinates,
    routePoints,
    setHoveredEventMarker,
    setHoveredPoint,
    setIsExpanded,
  } = useDriverRouteMapState(driverRoute);

  // Calculate map configuration based on order type
  const mapConfig = useMemo(() => {
    const isIndividualOrder = order.type === "INDIVIDUAL_PICKUP_AND_DROPOFF";

    if (isIndividualOrder && order.pickupLocation && order.dropoffLocation) {
      // For individual orders, use bounds that include both pickup and dropoff
      const bounds = {
        east: Math.max(
          order.pickupLocation.coordinates[0],
          order.dropoffLocation.coordinates[0],
        ),
        north: Math.max(
          order.pickupLocation.coordinates[1],
          order.dropoffLocation.coordinates[1],
        ),
        south: Math.min(
          order.pickupLocation.coordinates[1],
          order.dropoffLocation.coordinates[1],
        ),
        west: Math.min(
          order.pickupLocation.coordinates[0],
          order.dropoffLocation.coordinates[0],
        ),
      };

      // Add padding to bounds
      const padding = 0.001;
      const config = {
        defaultBounds: {
          east: bounds.east + padding,
          north: bounds.north + padding,
          south: bounds.south - padding,
          west: bounds.west - padding,
        },
      };
      return config;
    } else {
      // For single location orders, use center
      const location = order.pickupLocation || order.dropoffLocation;
      if (
        location &&
        location.coordinates &&
        location.coordinates.length >= 2
      ) {
        const config = {
          defaultCenter: {
            lat: location.coordinates[1],
            lng: location.coordinates[0],
          },
          defaultZoom: 15,
        };
        return config;
      }
    }

    // Fallback configuration
    const fallbackConfig = {
      defaultCenter: { lat: 40.7128, lng: -74.006 }, // NYC
      defaultZoom: 10,
    };
    return fallbackConfig;
  }, [order]);

  const eventMarkers = useMemo(() => {
    if (!driverRoute || !routePoints.length)
      return { missing: [], placed: [], skipped: [] };

    const events = [
      {
        ...EVENT_COLORS.IN_TRANSIT,
        name: "In Transit",
        timestamp: order.actualStartDateTime,
      },
      {
        ...EVENT_COLORS.PICKUP_ARRIVAL,
        name: "Pickup Arrival Time",
        timestamp: order.geoPickupDateTime,
      },
      {
        ...EVENT_COLORS.ACTUAL_PICKUP,
        name: "Actual Pickup Time",
        timestamp: order.actualPickupDateTime,
      },
      {
        ...EVENT_COLORS.DROPOFF_ARRIVAL,
        name: "Drop-off Arrival Time",
        timestamp: order.geoDropoffDateTime,
      },
      {
        ...EVENT_COLORS.ACTUAL_DROPOFF,
        name: "Actual Drop-off Time",
        timestamp: order.actualEndDateTime,
      },
    ];

    const placedEvents: EventMarker[] = [];
    const skippedEvents: EventBase[] = [];
    const missingEvents: EventBase[] = [];

    events.forEach((event) => {
      if (event.timestamp === null) {
        // No timestamp available
        missingEvents.push(event);
      } else {
        const { point: location, timeDiff } = findClosestLocation(
          event.timestamp,
          routePoints,
        );

        // Only place marker if driver location is within 45 seconds of event
        if (timeDiff <= 300000) {
          // 300000ms = 300 seconds
          placedEvents.push({
            ...event,
            lat: location.lat,
            lng: location.lng,
            timestamp: event.timestamp,
          });
        } else {
          skippedEvents.push({
            ...event,
            timestamp: event.timestamp,
          });
        }
      }
    });

    return {
      missing: missingEvents,
      placed: placedEvents,
      skipped: skippedEvents,
    };
  }, [
    driverRoute,
    routePoints,
    order.actualStartDateTime,
    order.geoPickupDateTime,
    order.actualPickupDateTime,
    order.geoDropoffDateTime,
    order.actualEndDateTime,
  ]);

  // Filter out route points that have event markers at the same location
  const filteredRoutePoints = useMemo(() => {
    if (!eventMarkers?.placed?.length) return routePoints;

    return routePoints.filter((point) => {
      return !eventMarkers.placed.some(
        (event) =>
          Math.abs(event.lat - point.lat) < 0.0001 &&
          Math.abs(event.lng - point.lng) < 0.0001,
      );
    });
  }, [routePoints, eventMarkers]);

  useEffect(() => {
    if (!mapInstance || !isSuccess || !driverRoute) return;

    const bounds = new google.maps.LatLngBounds();
    const firstEntry = driverRoute.locationEntries[0];
    const lastEntry =
      driverRoute.locationEntries[driverRoute.locationEntries.length - 1];

    bounds.extend({
      lat: firstEntry.coordinates.latitude,
      lng: firstEntry.coordinates.longitude,
    });
    bounds.extend({
      lat: lastEntry.coordinates.latitude,
      lng: lastEntry.coordinates.longitude,
    });

    mapInstance.fitBounds(bounds, 10);
  }, [mapInstance, isSuccess, driverRoute]);

  useEffect(() => {
    if (!expandedMapInstance || !isSuccess || !driverRoute) return;

    const bounds = new google.maps.LatLngBounds();

    // Include all route points in bounds for expanded view
    driverRoute.locationEntries.forEach((entry) => {
      bounds.extend({
        lat: entry.coordinates.latitude,
        lng: entry.coordinates.longitude,
      });
    });

    // Also include pickup/dropoff locations if they exist
    if (
      order.pickupLocation &&
      order.pickupLocation.coordinates[1] &&
      order.pickupLocation.coordinates[0]
    ) {
      bounds.extend({
        lat: order.pickupLocation.coordinates[1],
        lng: order.pickupLocation.coordinates[0],
      });
    }
    if (
      order.dropoffLocation &&
      order.dropoffLocation.coordinates[1] &&
      order.dropoffLocation.coordinates[0]
    ) {
      bounds.extend({
        lat: order.dropoffLocation.coordinates[1],
        lng: order.dropoffLocation.coordinates[0],
      });
    }

    expandedMapInstance.fitBounds(bounds, 20);
  }, [
    expandedMapInstance,
    isSuccess,
    driverRoute,
    order.pickupLocation?.coordinates[1],
    order.pickupLocation?.coordinates[0],
    order.dropoffLocation?.coordinates[1],
    order.dropoffLocation?.coordinates[0],
  ]);

  if (!isLoaded || isPending) {
    return <DriverRouteLoader isLoaded={isLoaded} isPending={isPending} />;
  }

  if (!isLoaded || isPending) {
    return <DriverRouteLoader isLoaded={isLoaded} isPending={isPending} />;
  }

  if (!isSuccess || !driverRoute) {
    return null;
  }

  return (
    <>
      {!isExpanded && (
        <div className="col-span-12 grid grid-cols-12 gap-4 lg:gap-6 px-4 md:px-6">
          <div className="w-full h-auto md:h-[450px] col-span-12 relative">
            <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
              <div style={{ height: "100%", width: "100%" }}>
                <Map
                  style={{ height: "400px", width: "100%" }}
                  defaultCenter={{ lat: 40.7128, lng: -74.006 }}
                  defaultZoom={13}
                  disableDefaultUI={true}
                  mapId={config.GOOGLE_MAPS_ID}
                >
                  <MapSetup onMapReady={setMapInstance} />
                  {order.type.includes("PICKUP") && order.pickupLocation && (
                    <Marker
                      type="pickup"
                      position={{
                        lat: order.pickupLocation.coordinates[1],
                        lng: order.pickupLocation.coordinates[0],
                      }}
                      icon={getOrderIcon(order, "PICKUP")}
                      scale={0.72}
                      routePositionColor={getRoutePositionColor(
                        order.statusText,
                      )}
                    />
                  )}
                  {order.type.includes("DROPOFF") && order.dropoffLocation && (
                    <Marker
                      type="dropoff"
                      position={{
                        lat: order.dropoffLocation.coordinates[1],
                        lng: order.dropoffLocation.coordinates[0],
                      }}
                      icon={getOrderIcon(order, "DROPOFF")}
                      scale={0.72}
                      routePositionColor={getRoutePositionColor(
                        order.statusText,
                      )}
                    />
                  )}
                  <MapPolyline
                    polylineOptions={getPolylineOptions(1, order.statusText)}
                    routeCoordinates={routeCoordinates}
                  />
                </Map>
              </div>
            </APIProvider>
            <button
              onClick={() => setIsExpanded(true)}
              className="absolute top-4 right-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-md p-2 shadow-sm transition-colors duration-200"
              title="Expand map"
            >
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
          </div>

          {order.estimatedDistance && (
            <div className="col-span-6 flex items-start flex-col gap-2">
              <div className="text-xs md:text-sm font-medium text-gray-700">
                Estimated Distance
              </div>
              <div className="text-xs md:text-sm text-gray-500">
                {`${getMetersToMiles(order.estimatedDistance)} miles`}
              </div>
            </div>
          )}
          <div className="col-span-6 flex items-start flex-col gap-2">
            <div className="text-xs md:text-sm font-medium text-gray-700">
              Actual Distance
            </div>
            <div className="text-xs md:text-sm text-gray-500">
              {actualDistance}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Map Modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Driver Timeline - {assignee}
              </h2>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Close expanded view"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 relative">
              {/* Event Legend Card */}
              <div className="absolute top-9 right-9 z-10 bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Events
                </div>
                {eventMarkers?.placed?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-600 mb-1">Available:</div>
                    {eventMarkers.placed.map((event, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-xs mb-1"
                      >
                        <div
                          className="w-3 h-3 rounded-full border border-white"
                          style={{ backgroundColor: event.color }}
                        ></div>
                        <span className="text-gray-700">{event.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {eventMarkers?.skipped?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-600 mb-1">
                      Unavailable:
                    </div>
                    {eventMarkers.skipped.map((event, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-xs mb-1"
                      >
                        <div className="w-3 h-3 rounded-full bg-gray-300 border border-gray-400"></div>
                        <span className="text-gray-500">{event.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {eventMarkers?.missing?.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-600 mb-1">
                      Missing Timestamp:
                    </div>
                    {eventMarkers.missing.map((event, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-xs mb-1"
                      >
                        <div className="w-3 h-3 rounded-full bg-gray-200 border border-gray-300"></div>
                        <span className="text-gray-400">{event.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {!eventMarkers?.placed?.length &&
                  !eventMarkers?.skipped?.length &&
                  !eventMarkers?.missing?.length && (
                    <div className="text-xs text-gray-500">
                      No events available
                    </div>
                  )}
              </div>
              <div className="w-full h-full">
                <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
                  <Map
                    style={{ height: "100%", width: "100%" }}
                    className="rounded-lg w-full h-full"
                    defaultCenter={
                      "defaultCenter" in mapConfig
                        ? mapConfig.defaultCenter
                        : undefined
                    }
                    defaultZoom={
                      "defaultZoom" in mapConfig
                        ? mapConfig.defaultZoom
                        : undefined
                    }
                    defaultBounds={
                      "defaultBounds" in mapConfig
                        ? mapConfig.defaultBounds
                        : undefined
                    }
                    disableDefaultUI={true}
                    mapId={config.GOOGLE_MAPS_ID}
                  >
                    <MapSetup onMapReady={setExpandedMapInstance} />
                    {order.type.includes("PICKUP") && order.pickupLocation && (
                      <AdvancedMarker
                        position={{
                          lat: order.pickupLocation.coordinates[1],
                          lng: order.pickupLocation.coordinates[0],
                        }}
                      >
                        <div
                          style={{
                            backgroundImage: `url(${getOrderIcon(order, "PICKUP")})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "contain",
                            height: "24px",
                            width: "20px",
                          }}
                        />
                      </AdvancedMarker>
                    )}
                    {order.type.includes("DROPOFF") &&
                      order.dropoffLocation && (
                        <AdvancedMarker
                          position={{
                            lat: order.dropoffLocation.coordinates[1],
                            lng: order.dropoffLocation.coordinates[0],
                          }}
                        >
                          <div
                            style={{
                              backgroundImage: `url(${getOrderIcon(order, "DROPOFF")})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "contain",
                              height: "24px",
                              width: "20px",
                            }}
                          />
                        </AdvancedMarker>
                      )}
                    <MapPolyline
                      polylineOptions={getPolylineOptions(1, order.statusText)}
                      routeCoordinates={routeCoordinates}
                    />
                    {filteredRoutePoints.map((point, index) => (
                      <AdvancedMarker
                        key={index}
                        position={{ lat: point.lat, lng: point.lng }}
                        onMouseEnter={() => setHoveredPoint(point)}
                        onMouseLeave={() => setHoveredPoint(null)}
                      >
                        <div
                          style={{
                            backgroundColor: "#94A3B8",
                            border: "1px solid #64748B",
                            borderRadius: "50%",
                            height: "6px",
                            opacity: 0.6,
                            width: "6px",
                          }}
                        />
                      </AdvancedMarker>
                    ))}
                    {hoveredPoint && (
                      <InfoWindow
                        position={{
                          lat: hoveredPoint.lat + 0.0001, // Slight offset to avoid mouse interference
                          lng: hoveredPoint.lng,
                        }}
                        pixelOffset={[0, -10]}
                        disableAutoPan
                      >
                        <div className="p-2">
                          <div className="text-sm font-medium">{assignee}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            <DateTimeTz
                              start={new Date(hoveredPoint.timestamp)}
                              timezone={order.timezone}
                              presentation="default"
                              includeSeconds
                            />
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                    {/* Event Markers */}
                    {eventMarkers?.placed?.map((event, index) => (
                      <AdvancedMarker
                        key={`expanded-event-${index}`}
                        position={{ lat: event.lat, lng: event.lng }}
                        onMouseEnter={() => setHoveredEventMarker(event)}
                        onMouseLeave={() => setHoveredEventMarker(null)}
                      >
                        <div
                          style={{
                            backgroundColor: event.color,
                            border: "3px solid #FFFFFF",
                            borderRadius: "50%",
                            height: "16px",
                            width: "16px",
                          }}
                        />
                      </AdvancedMarker>
                    ))}
                    {/* Event Marker Hover InfoWindow for Expanded View */}
                    {hoveredEventMarker && (
                      <InfoWindow
                        position={{
                          lat: hoveredEventMarker.lat + 0.0001, // Slight offset to avoid mouse interference
                          lng: hoveredEventMarker.lng,
                        }}
                        pixelOffset={[0, -10]}
                        disableAutoPan
                      >
                        <div className="p-2">
                          <div className="text-sm font-semibold text-gray-900 mb-1">
                            {hoveredEventMarker.name}
                          </div>
                          <div className="text-xs text-gray-600">
                            <DateTimeTz
                              start={new Date(hoveredEventMarker.timestamp)}
                              timezone={order.timezone}
                              presentation="default"
                              includeSeconds
                            />
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Map>
                </APIProvider>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

const DriverRouteLoader = memo(function DriverRouteLoader({
  isLoaded,
  isPending,
}: {
  isLoaded: boolean;
  isPending: boolean;
}) {
  return (
    <div className="px-6">
      <Skeleton
        height="h-[250px] rounded-lg"
        width="col-span-12"
        text={
          !isLoaded
            ? "Loading map..."
            : isPending
              ? "Fetching route data..."
              : ""
        }
      />
      <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-5">
        <div className="col-span-6">
          <Skeleton width="w-[80%]" height="h-[14px]" />
          <Skeleton width="w-[55%]" height="h-[14px] mt-2" />
        </div>
        <div className="col-span-6">
          <Skeleton width="w-[80%]" height="h-[14px]" />
          <Skeleton width="w-[55%]" height="h-[14px] mt-2" />
        </div>
      </div>
    </div>
  );
});

export default DriverRoute;
